import { Form } from '$components'
import { z } from 'zod'
import { createContext, useContext, ReactNode } from 'react'
import { UpdateCoatTypeFormContent } from './content'
import { UpdateCoatTypeFormControls } from './controls'

export enum Input {
  Name = 'name',
}

export const updateCoatTypeSchema = z.object({
  [Input.Name]: z.string()
    .min(1, { message: 'Name is required.' })
    .max(255, { message: 'Name must be less than 255 characters.' }),
})

export type UpdateCoatTypeSchema = z.infer<typeof updateCoatTypeSchema>

type UpdateCoatTypeFormContextType = {
  onSubmit: (values: UpdateCoatTypeSchema) => void
  onCancel: () => void
  isLoading?: boolean
}

const UpdateCoatTypeFormContext = createContext<UpdateCoatTypeFormContextType | undefined>(undefined)

type UpdateCoatTypeFormProps = UpdateCoatTypeFormContextType & {
  children?: ReactNode
  defaultValues?: Partial<UpdateCoatTypeSchema>
}

export function UpdateCoatTypeForm({
  onSubmit,
  onCancel,
  isLoading = false,
  children,
  defaultValues,
}: UpdateCoatTypeFormProps) {
  const context: UpdateCoatTypeFormContextType = {
    onSubmit,
    onCancel,
    isLoading,
  }

  return (
    <UpdateCoatTypeFormContext.Provider value={context}>
      <Form<UpdateCoatTypeSchema, typeof updateCoatTypeSchema>
        schema={updateCoatTypeSchema}
        onSubmit={onSubmit}
        defaultValues={defaultValues}
      >
        {children}
      </Form>
    </UpdateCoatTypeFormContext.Provider>
  )
}

export function useUpdateCoatTypeForm() {
  const context = useContext(UpdateCoatTypeFormContext)
  if (context === undefined) {
    throw new Error('useUpdateCoatTypeForm must be used within an UpdateCoatTypeForm')
  }
  return context
}

UpdateCoatTypeForm.Content = UpdateCoatTypeFormContent
UpdateCoatTypeForm.Controls = UpdateCoatTypeFormControls
